import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../../hooks/useBooking';
import { hexToRgba } from '../../lib/colorUtils';
import { accentPillStyle, primaryButtonStyle, useSiteColors } from '../../hooks/useSiteColors';

interface HeroSectionProps {
  layout: 'split' | 'fullbleed' | 'centered';
}

// Text over a cover photo sits on a dark scrim, so it stays white whatever the site colors are.
export function HeroSection({ layout }: HeroSectionProps) {
  const navigate = useNavigate();
  const { businessListing } = useBooking();
  const c = useSiteColors();

  const galleryImages = businessListing?.cover_images ?? [];
  const [activeSlide, setActiveSlide] = useState(0);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep activeSlide in bounds
  useEffect(() => {
    if (galleryImages.length > 0 && activeSlide >= galleryImages.length) {
      setActiveSlide(0);
    }
  }, [galleryImages.length, activeSlide]);

  // Auto-advance (only for split layout)
  useEffect(() => {
    if (layout !== 'split') return;
    if (galleryImages.length <= 1) return;
    autoplayRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % galleryImages.length);
    }, 5000);
    return () => { if (autoplayRef.current) clearInterval(autoplayRef.current); };
  }, [galleryImages.length, layout]);

  const goToSlide = useCallback((index: number) => {
    setActiveSlide(index);
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    if (galleryImages.length > 1) {
      autoplayRef.current = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % galleryImages.length);
      }, 5000);
    }
  }, [galleryImages.length]);

  const prevSlide = useCallback(() => {
    goToSlide(activeSlide <= 0 ? galleryImages.length - 1 : activeSlide - 1);
  }, [activeSlide, galleryImages.length, goToSlide]);

  const nextSlide = useCallback(() => {
    goToSlide((activeSlide + 1) % galleryImages.length);
  }, [activeSlide, galleryImages.length, goToSlide]);

  if (!businessListing) return null;

  const headline = businessListing.hero_headline ?? `Welcome to ${businessListing.business_name}`;
  const subtext = businessListing.hero_subtext
    ?? (businessListing.about
      ? (businessListing.about.length > 200
        ? businessListing.about.substring(0, 200).trim() + '...'
        : businessListing.about)
      : `Book your next appointment with ${businessListing.business_name} in seconds.`);
  const ctaLabel = businessListing.cta_label ?? 'Book Appointment';
  const hasPhotoGallery = galleryImages.length > 0;

  const scrollToServices = () => {
    const el = document.getElementById('services');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Split layout (current behavior) ────────────────────────────
  if (layout === 'split') {
    return (
      <section className="pt-[140px] pb-20 relative overflow-hidden">
        <div
          className="absolute top-0 right-[-10%] w-[50%] h-[70%] pointer-events-none"
          style={{ background: `radial-gradient(ellipse at center, ${hexToRgba(c.primaryFill, 0.08)}, transparent 70%)` }}
        />
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <div>
              {businessListing.tagline && (
                <div
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-6"
                  style={accentPillStyle(c)}
                >
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: c.onAccent }} />
                  {businessListing.tagline}
                </div>
              )}
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight mb-5"
                style={{ color: c.text }}
              >
                {businessListing.hero_headline ? (
                  headline
                ) : (
                  <>Welcome to{' '}<span style={{ color: c.link }}>{businessListing.business_name}</span></>
                )}
              </h1>
              <p className="text-lg leading-relaxed max-w-lg mb-9" style={{ color: c.mutedText }}>
                {subtext}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/book')}
                  className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full text-[15px] font-semibold transition-all hover:-translate-y-0.5"
                  style={primaryButtonStyle(c)}
                >
                  {ctaLabel}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={scrollToServices}
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-[15px] font-semibold transition-all border-[1.5px]"
                  style={{ color: c.text, borderColor: c.border }}
                >
                  View Services
                </button>
              </div>
            </div>

            {hasPhotoGallery && (
              <div className="relative h-[400px] sm:h-[480px] lg:h-[520px]">
                <div className="group absolute inset-0 rounded-3xl overflow-hidden shadow-xl">
                  {galleryImages.map((src, i) => (
                    <img
                      key={`${src}-${i}`}
                      src={src}
                      alt={`${businessListing.business_name} ${i + 1}`}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out"
                      style={{ opacity: i === activeSlide ? 1 : 0 }}
                    />
                  ))}
                  {galleryImages.length > 1 && (
                    <>
                      <button
                        onClick={prevSlide}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-black/50 transition-all duration-300 z-10"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={nextSlide}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-black/50 transition-all duration-300 z-10"
                        aria-label="Next image"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                        {galleryImages.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => goToSlide(i)}
                            className="transition-all duration-300 rounded-full"
                            style={{
                              width: i === activeSlide ? 24 : 8,
                              height: 8,
                              backgroundColor: i === activeSlide ? '#fff' : 'rgba(255,255,255,0.5)',
                            }}
                            aria-label={`Go to image ${i + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // ── Fullbleed layout ────────────────────────────────────────────
  if (layout === 'fullbleed') {
    const bgImage = galleryImages[0];
    // Quoted url() so `&` in Firebase download URLs is not mis-parsed as CSS (unquoted url() + query string).
    const bgImageCss = bgImage
      ? `url("${bgImage.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")`
      : undefined;
    return (
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {bgImage ? (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: bgImageCss,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        ) : (
          <div className="absolute inset-0" style={{ backgroundColor: c.primaryFill }} />
        )}
        {bgImage && (
          <>
            {/* Dark scrim so white text reads on any photo */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.2) 100%)' }} />
            <div
              className="absolute bottom-0 left-0 right-0 h-[30%] pointer-events-none"
              style={{ background: `linear-gradient(to top, ${hexToRgba(c.primaryFill, 0.3)}, transparent)` }}
            />
          </>
        )}
        <div className="relative z-10 text-center px-6 max-w-[800px] mx-auto">
          {businessListing.tagline && (
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-8"
              style={accentPillStyle(c)}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: c.onAccent }} />
              {businessListing.tagline}
            </div>
          )}
          <h1
            className="font-bold leading-[1.05] tracking-tight mb-6"
            style={{ fontSize: 'clamp(3rem, 6vw, 5.5rem)', color: bgImage ? '#FFFFFF' : c.onPrimary }}
          >
            {headline}
          </h1>
          <p className="text-lg leading-relaxed mb-10 max-w-lg mx-auto" style={{ color: bgImage ? 'rgba(255,255,255,0.85)' : c.onPrimary }}>
            {subtext}
          </p>
          <button
            onClick={() => navigate('/book')}
            className="inline-flex items-center justify-center gap-2.5 px-10 py-4 rounded-full text-[15px] font-semibold transition-all hover:-translate-y-0.5 w-full sm:w-auto"
            style={
              bgImage
                ? primaryButtonStyle(c, 0.4)
                : { backgroundColor: c.onPrimary, color: c.primaryFill }
            }
          >
            {ctaLabel}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    );
  }

  // ── Centered layout ─────────────────────────────────────────────
  return (
    <section className="pt-[160px] pb-24 relative overflow-hidden">
      <div className="max-w-[640px] mx-auto px-6 text-center">
        {businessListing.logo_url && (
          <img
            src={businessListing.logo_url}
            alt={businessListing.business_name}
            className="h-16 object-contain mx-auto mb-8"
          />
        )}
        {businessListing.tagline && (
          <p className="text-xs font-semibold tracking-[0.15em] uppercase mb-4" style={{ color: c.link }}>
            {businessListing.tagline}
          </p>
        )}
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight mb-6"
          style={{ color: c.text }}
        >
          {headline}
        </h1>
        <p className="text-lg leading-relaxed mb-9" style={{ color: c.mutedText }}>
          {subtext}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate('/book')}
            className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full text-[15px] font-semibold transition-all hover:-translate-y-0.5"
            style={primaryButtonStyle(c)}
          >
            {ctaLabel}
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={scrollToServices}
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-[15px] font-semibold transition-all border-[1.5px]"
            style={{ color: c.text, borderColor: c.border }}
          >
            View Services
          </button>
        </div>
      </div>
    </section>
  );
}
